import { Controller } from "@hotwired/stimulus"

// Me's voice row (persona/_twin_voice): record a minute of talking with the
// microphone (or pick a file), play it back, confirm it's your own voice, and
// only then send it. A recording lands in the row's file input, so the one
// hidden form carries both paths. "Hear it" plays the cloned voice.
const LONGEST = 3 * 60

export default class extends Controller {
  static targets = ["input", "record", "recordLabel", "timer", "player", "consent", "submit", "hint"]

  disconnect() {
    this._stop()
    this._release()
    if (this._url) URL.revokeObjectURL(this._url)
  }

  async toggle(event) {
    event.preventDefault()
    if (this._recorder && this._recorder.state === "recording") return this._stop()
    try {
      // The microphone as it is: the browser's call-quality processing
      // (echo cancellation, noise suppression, auto gain) smears exactly the
      // timbre a voice clone learns from.
      this._stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false, channelCount: 1 }
      })
    } catch (_error) {
      this.hintTarget.textContent = "Jane couldn't use your microphone. Allow it, or choose a recording instead."
      return
    }
    const type = ["audio/webm", "audio/mp4", "audio/ogg"].find((kind) => window.MediaRecorder?.isTypeSupported?.(kind)) || ""
    this._chunks = []
    // Well above ElevenLabs' 128 kbps floor for a clone sample.
    const options = { audioBitsPerSecond: 192000 }
    if (type) options.mimeType = type
    this._recorder = new MediaRecorder(this._stream, options)
    this._recorder.addEventListener("dataavailable", (e) => e.data.size && this._chunks.push(e.data))
    this._recorder.addEventListener("stop", () => this._recorded())
    this._recorder.start()
    this._started = Date.now()
    this.recordLabelTarget.textContent = "Stop"
    this.timerTarget.hidden = false
    this._tick()
    this._clock = setInterval(() => this._tick(), 500)
  }

  picked(event) {
    // Inside the persona editor; a recording isn't an unsaved change to it.
    event.stopPropagation()
    const file = this.inputTarget.files && this.inputTarget.files[0]
    if (file) this._ready(file)
  }

  consented(event) {
    event.stopPropagation()
    this._sync()
  }

  preview(event) {
    event.preventDefault()
    const url = event.params.url
    if (!this._audio) this._audio = new Audio()
    this._audio.src = `${url}&t=${Date.now()}`
    this._audio.play().catch(() => {})
  }

  _tick() {
    const seconds = Math.floor((Date.now() - this._started) / 1000)
    this.timerTarget.textContent = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`
    if (seconds >= LONGEST) this._stop()
  }

  _stop() {
    clearInterval(this._clock)
    if (this._recorder && this._recorder.state === "recording") this._recorder.stop()
  }

  _release() {
    this._stream?.getTracks().forEach((track) => track.stop())
    this._stream = null
  }

  _recorded() {
    this._release()
    this.recordLabelTarget.textContent = "Record again"
    const type = (this._recorder.mimeType || "audio/webm").split(";")[0]
    const extension = { "audio/mp4": "m4a", "audio/ogg": "ogg" }[type] || "webm"
    const file = new File(this._chunks, `my-voice.${extension}`, { type })
    const transfer = new DataTransfer()
    transfer.items.add(file)
    this.inputTarget.files = transfer.files
    this._ready(file)
  }

  _ready(file) {
    if (this._url) URL.revokeObjectURL(this._url)
    this._url = URL.createObjectURL(file)
    this.playerTarget.src = this._url
    this.playerTarget.hidden = false
    const seconds = this._started ? Math.round((Date.now() - this._started) / 1000) : null
    this.hintTarget.textContent = seconds !== null && seconds < 30
      ? "That's short - a minute or more sounds much more like you."
      : "Have a listen. Sounds right? Confirm it's you and use it."
    this._sync()
  }

  _sync() {
    const hasRecording = this.inputTarget.files && this.inputTarget.files.length > 0
    this.submitTarget.disabled = !(hasRecording && this.consentTarget.checked)
  }
}
